/**
 * The newest message seen in each thread, as a module-level store rather than a React
 * context.
 *
 * ChatUnreadContext already subscribes to every messages_new INSERT this viewer is allowed
 * to see, so the facts a chat list row needs -- what the last message says and when it
 * landed -- arrive there for free. They are published here instead of on that context's
 * value because NavBar reads that value on every screen: folding previews into it would
 * re-render the whole nav on every message anywhere, for a string it never shows.
 *
 * Same shape as utils/activeChatKey.ts, for the same reason.
 */

export type ChatActivity = {
  /** messages_new.created_at, exactly as realtime delivered it. */
  lastMessageAt: string;
  /** Already run through formatChatMessagePreview(). */
  preview: string;
};

let activity: ReadonlyMap<string, ChatActivity> = new Map();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * The current map, keyed by chatKey().
 *
 * The reference is stable until something actually changes, so useSyncExternalStore can
 * use it as its own snapshot without looping.
 */
export function getChatActivity(): ReadonlyMap<string, ChatActivity> {
  return activity;
}

/**
 * Record a thread's newest message.
 *
 * Newest wins: an entry that is not strictly newer than the one already held is dropped.
 * Realtime gives no ordering guarantee across a reconnect, and the same row can be
 * delivered twice, so without this a row could visibly roll backwards.
 */
export function noteChatActivity(key: string, next: ChatActivity): void {
  const at = new Date(next.lastMessageAt).getTime();
  if (Number.isNaN(at)) return;

  const current = activity.get(key);
  if (current && new Date(current.lastMessageAt).getTime() >= at) return;

  const map = new Map(activity);
  map.set(key, next);
  activity = map;
  emit();
}

export function subscribeChatActivity(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Signing out, or switching account: one viewer must never see another's previews. */
export function resetChatActivity(): void {
  if (activity.size === 0) return;
  activity = new Map();
  emit();
}
